import { useCallback, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { fallbackSrc, srcset, type Photo } from "@/lib/photos";

type BeforeAfterProps = {
  before: Photo;
  after: Photo;
  className?: string;
};

const SIZES = "(min-width: 1024px) 40vw, 100vw";
const PRESETS = [25, 50, 75] as const;

export function BeforeAfter({ before, after, className }: BeforeAfterProps) {
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);

  const setFromClientX = useCallback((clientX: number) => {
    const el = frameRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0) return;
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, next)));
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    setFromClientX(e.clientX);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setFromClientX(e.clientX);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setDragging(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 10 : 2;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setPos(p => Math.max(0, p - step));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setPos(p => Math.min(100, p + step));
    } else if (e.key === "Home") {
      e.preventDefault();
      setPos(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setPos(100);
    }
  };

  const rounded = Math.round(pos * 10) / 10;

  return (
    <figure className={cn("group", className)}>
      <div className="border border-border/70 bg-background p-2.5 transition-colors duration-700 group-hover:border-gold/70 sm:p-4">
        <div className="relative overflow-hidden bg-muted">
          <div
            ref={frameRef}
            role="slider"
            tabIndex={0}
            aria-label="Reveal the transformation: left is before, right is after"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={rounded}
            aria-valuetext={`${rounded}% before, ${100 - rounded}% after`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onKeyDown={onKeyDown}
            className={cn(
              "relative aspect-[3/4] w-full touch-none select-none",
              dragging ? "cursor-grabbing" : "cursor-ew-resize",
            )}
          >
            <img
              src={fallbackSrc(after)}
              srcSet={srcset(after)}
              sizes={SIZES}
              alt={after.alt}
              width={after.width}
              height={after.height}
              loading="lazy"
              decoding="async"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
              draggable={false}
            />

            <div
              className="pointer-events-none absolute inset-0"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            >
              <img
                src={fallbackSrc(before)}
                srcSet={srcset(before)}
                sizes={SIZES}
                alt={before.alt}
                width={before.width}
                height={before.height}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover"
                draggable={false}
              />
            </div>

            <div
              className="pointer-events-none absolute inset-y-0 z-20 w-px bg-gold/80 shadow-[0_0_18px_rgba(0,0,0,0.28)]"
              style={{ left: `${pos}%` }}
            >
              <span
                className={cn(
                  "absolute top-1/2 left-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-0.5 rounded-full border border-gold/50 bg-background/95 text-foreground shadow-[0_6px_24px_rgba(0,0,0,0.22)] backdrop-blur-sm transition-transform duration-300",
                  dragging ? "scale-105" : "scale-100 group-hover:scale-105",
                )}
              >
                <ChevronLeft className="h-4 w-4" strokeWidth={1.25} />
                <ChevronRight className="h-4 w-4" strokeWidth={1.25} />
              </span>
            </div>

            <span className="pointer-events-none absolute top-4 left-4 rounded-full bg-background/85 px-3 py-1.5 font-sans text-[0.625rem] font-medium tracking-[0.28em] text-foreground uppercase backdrop-blur-sm">
              Before
            </span>
            <span className="pointer-events-none absolute top-4 right-4 rounded-full bg-espresso/85 px-3 py-1.5 font-sans text-[0.625rem] font-medium tracking-[0.28em] text-background uppercase backdrop-blur-sm">
              After
            </span>
          </div>

          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 border border-foreground/10"
          />
        </div>
      </div>

      <figcaption className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
        <span className="font-sans text-[0.625rem] tracking-[0.24em] text-muted-foreground uppercase">
          Drag to reveal
        </span>

        <div
          className="flex items-center gap-1.5"
          role="group"
          aria-label="Jump the comparison to a preset position"
        >
          {PRESETS.map(p => {
            const active = Math.round(pos) === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => setPos(p)}
                aria-pressed={active}
                className={cn(
                  "h-9 min-w-12 cursor-pointer rounded-full border px-3 font-sans text-[0.625rem] tracking-[0.16em] uppercase transition-all duration-500",
                  active
                    ? "border-gold bg-gold/12 text-gold"
                    : "border-border text-muted-foreground hover:border-gold/60 hover:text-foreground",
                )}
              >
                {p}%
              </button>
            );
          })}
        </div>
      </figcaption>
    </figure>
  );
}
